import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import { sameId } from '../../../shared/api/wire'
import { dropResource } from '../../../shared/model/keys'
import { cartEdited, newSaleStarted, saleSeatCleared, seatKey, type SeatRef } from './saleSlice'

/**
 * «Быстрая оплата» (решение владельца 24.10.2026, RP-SALE-FAST шаг 9): после подтверждённой оплаты картой продажа откладывается на сервере,
 * а её чек печатается в фоне — кассир сразу набирает следующую продажу. Здесь — память вкладки о таких продажах: чтобы показать «Чек
 * пробит № …» после того, как продажа ушла из списка отложенных, и не открыть окно итога поверх новой корзины. Сам ход чека читается с
 * сервера (список отложенных продаж), поэтому после F5 панель собирается заново, а память — нет.
 */
export interface FastReceiptNote {
  resourceId: string
  registerKey: string
  /** Касса кабинета: список отложенных продаж читается по ней. */
  deviceId: number
  saleId: string
  /** Когда продажа ушла в фон (ISO). */
  handedOffAt: string
  /** Когда чек пробит (ISO); `null` — ещё идёт или с ним проблема. */
  doneAt: string | null
}

export interface FastReceiptsState {
  /** Ключ — `<rid>|<registerKey>`. */
  bySeat: Record<string, FastReceiptNote[]>
  /** Продажа, которая только что ушла в фон: окно оплаты показывает галочку «Оплата прошла» и закрывается само. */
  flash: Record<string, string>
}

const initialState: FastReceiptsState = { bySeat: {}, flash: {} }

/** Закрытые записи старше этого срока выбрасываются при следующем уходе в фон: панель их давно не показывает. */
const KEEP_DONE_MS = 60_000

interface Ref extends SeatRef {
  saleId: string
}

interface HandOffRef extends Ref {
  deviceId: number
}

const fastReceiptsSlice = createSlice({
  name: 'aqsi/fastReceipts',
  initialState,
  reducers: {
    /** Продажа отложена службой в ответ на быструю оплату: чек печатается в фоне. */
    fastReceiptHandedOff: {
      reducer(state, action: PayloadAction<HandOffRef & { at: string }>) {
        const key = seatKey(action.payload)
        const now = Date.parse(action.payload.at)
        const notes = (state.bySeat[key] ?? []).filter(
          (note) => note.doneAt === null || Number.isNaN(now) || now - Date.parse(note.doneAt) < KEEP_DONE_MS,
        )
        if (!notes.some((note) => sameId(note.saleId, action.payload.saleId))) {
          const { resourceId, registerKey, deviceId, saleId, at } = action.payload
          notes.push({ resourceId, registerKey, deviceId, saleId, handedOffAt: at, doneAt: null })
        }
        state.bySeat[key] = notes
        state.flash[key] = action.payload.saleId
      },
      prepare(ref: HandOffRef) {
        return { payload: { ...ref, at: new Date().toISOString() } }
      },
    },
    /** Чек фоновой продажи пробит. */
    fastReceiptDone: {
      reducer(state, action: PayloadAction<Ref & { at: string }>) {
        const note = state.bySeat[seatKey(action.payload)]?.find((item) => sameId(item.saleId, action.payload.saleId))
        if (note !== undefined && note.doneAt === null) note.doneAt = action.payload.at
      },
      prepare(ref: Ref) {
        return { payload: { ...ref, at: new Date().toISOString() } }
      },
    },
    /** «Понятно» у «Чек пробит» или продажа вернулась на кассу: память о ней больше не нужна. */
    fastReceiptDismissed(state, action: PayloadAction<Ref>) {
      const key = seatKey(action.payload)
      const notes = state.bySeat[key]
      if (notes === undefined) return
      const rest = notes.filter((note) => !sameId(note.saleId, action.payload.saleId))
      if (rest.length === 0) delete state.bySeat[key]
      else state.bySeat[key] = rest
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => {
      dropResource(state.bySeat, action.payload.resourceId)
      dropResource(state.flash, action.payload.resourceId)
    })
    // Галочка «Оплата прошла» заканчивается началом новой продажи (она закрывает окно сама) или первой правкой корзины.
    builder.addCase(newSaleStarted, (state, action) => {
      delete state.flash[seatKey(action.payload)]
    })
    builder.addCase(cartEdited, (state, action) => {
      delete state.flash[seatKey(action.payload)]
    })
    builder.addCase(saleSeatCleared, (state, action) => {
      delete state.flash[seatKey(action.payload)]
    })
  },
})

export const { fastReceiptHandedOff, fastReceiptDone, fastReceiptDismissed } = fastReceiptsSlice.actions
export const fastReceiptsReducer = fastReceiptsSlice.reducer

const NO_NOTES: readonly FastReceiptNote[] = []

export function fastReceiptNotesOf(state: FastReceiptsState, ref: SeatRef): readonly FastReceiptNote[] {
  return state.bySeat[seatKey(ref)] ?? NO_NOTES
}

export function fastReceiptFlashOf(state: FastReceiptsState, ref: SeatRef): string | null {
  return state.flash[seatKey(ref)] ?? null
}

/** Эта продажа ушла в фон по быстрой оплате и её чек ещё не пробит. */
export function isFastReceiptPending(state: FastReceiptsState | undefined, ref: SeatRef, saleId: string): boolean {
  return state !== undefined && fastReceiptNotesOf(state, ref).some((note) => note.doneAt === null && sameId(note.saleId, saleId))
}
